import type { JSX } from "react";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type ExpensePlanItem, type RetirementTarget } from "../api.js";
import { Money } from "../components/Money.js";
import { Modal } from "../components/Modal.js";
import { Field, TextInput } from "../components/Form.js";
import { PageHeader } from "../components/PageHeader.js";
import { StatCard } from "../components/StatCard.js";
import { Gauge } from "../components/Gauge.js";
import { Button } from "../components/ui/button.js";
import { Card } from "../components/ui/card.js";
import { Input } from "../components/ui/input.js";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table.js";
import { majorToMinor, minorToMajorInput } from "../money.js";

const STAT_GRID = "grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4";

export function Retirement(): JSX.Element {
  const qc = useQueryClient();
  const [editingTarget, setEditingTarget] = useState<RetirementTarget | null>(null);
  const [editingItem, setEditingItem] = useState<ExpensePlanItem | null>(null);
  const [showTargetForm, setShowTargetForm] = useState(false);
  const [showItemForm, setShowItemForm] = useState(false);
  const [targetYearInput, setTargetYearInput] = useState<string>("");
  const targetYearNum = targetYearInput ? Number(targetYearInput) : undefined;

  const targets = useQuery({
    queryKey: ["targets"],
    queryFn: () => api.list<RetirementTarget>("/retirement/targets"),
  });
  const plan = useQuery({
    queryKey: ["plan"],
    queryFn: () => api.list<ExpensePlanItem>("/retirement/expense-plan"),
  });
  const projections = useQuery({
    queryKey: ["retirement-projections", targetYearNum],
    queryFn: () => api.retirementProjections(targetYearNum),
  });
  const removeTarget = useMutation({
    mutationFn: (id: string) => api.remove(`/retirement/targets/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["targets"] }),
  });
  const removeItem = useMutation({
    mutationFn: (id: string) => api.remove(`/retirement/expense-plan/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["plan"] }),
  });

  const projByTarget = new Map((projections.data?.targets ?? []).map((p) => [p.target_id, p]));

  return (
    <>
      <PageHeader title="Retirement" />

      {/* Snapshot + target-year input */}
      <div className={`${STAT_GRID} mb-6`}>
        <StatCard
          label="Current retirement balance"
          value={
            projections.data ? (
              <Money amountMinor={projections.data.current_balance_eur_minor} native="EUR" block />
            ) : (
              "—"
            )
          }
          sub={'Mark accounts as "retirement" on the Accounts page to count them.'}
        />
        <StatCard
          label="Adding (3-month avg)"
          value={
            projections.data ? (
              <>
                <Money
                  amountMinor={projections.data.current_monthly_contribution_eur_minor}
                  native="EUR"
                />{" "}
                / mo
              </>
            ) : (
              "—"
            )
          }
          sub="Credits to retirement accounts (excl. internal transfers)."
        />
        <Card className="gap-1.5 p-5">
          <span className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
            Want to retire by year
          </span>
          <Input
            type="number"
            placeholder={String(new Date().getUTCFullYear() + 20)}
            value={targetYearInput}
            onChange={(e) => {
              setTargetYearInput(e.target.value);
            }}
          />
          <span className="text-muted-foreground text-xs">
            Each target shows the monthly contribution needed.
          </span>
        </Card>
      </div>

      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-lg font-semibold">Targets</h3>
        <Button
          onClick={() => {
            setEditingTarget(null);
            setShowTargetForm(true);
          }}
        >
          + Add target
        </Button>
      </div>
      <div className={STAT_GRID}>
        {targets.data?.items.map((t) => {
          const p = projByTarget.get(t.id);
          const swrPct = Number(t.safe_withdrawal_rate) * 100;
          const progress =
            p && t.fire_amount_minor > 0
              ? Math.min(
                  1,
                  p.required_monthly_to_hit_target_year_minor === null
                    ? (projections.data?.current_balance_eur_minor ?? 0) / t.fire_amount_minor
                    : 0,
                )
              : 0;
          return (
            <Card key={t.id} className="gap-1.5 p-5">
              <span className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                {t.label}
              </span>
              <span className="text-2xl font-semibold">
                <Money amountMinor={t.fire_amount_minor} native={t.currency} block />
              </span>
              <span className="text-muted-foreground text-xs">SWR {swrPct.toFixed(1)}%</span>
              {p && (
                <div className="mt-3 border-t pt-3 leading-relaxed">
                  <div>
                    At retirement:{" "}
                    <strong>
                      <Money amountMinor={p.monthly_income_at_retirement_minor} native={p.currency} />{" "}
                      / mo
                    </strong>
                  </div>
                  <div className="text-muted-foreground text-xs">
                    {p.years_to_target === null
                      ? "On current pace: not reachable in 100 years"
                      : p.years_to_target === 0
                        ? "Already met"
                        : `On track in ${p.years_to_target.toFixed(1)} years`}
                  </div>
                  {p.required_monthly_to_hit_target_year_minor !== null && (
                    <div className="text-warn mt-1 text-xs">
                      To hit by {targetYearNum}: add{" "}
                      <strong>
                        <Money
                          amountMinor={p.required_monthly_to_hit_target_year_minor}
                          native="EUR"
                        />{" "}
                        / mo
                      </strong>
                    </div>
                  )}
                  {progress > 0 && <Gauge className="mt-2" value={progress} />}
                </div>
              )}
              <div className="mt-3 flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setEditingTarget(t);
                    setShowTargetForm(true);
                  }}
                >
                  Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (confirm(`Delete ${t.label}?`)) removeTarget.mutate(t.id);
                  }}
                >
                  Del
                </Button>
              </div>
            </Card>
          );
        })}
      </div>

      <div className="mt-8 mb-3 flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-lg font-semibold">Expense plan</h3>
        <Button
          onClick={() => {
            setEditingItem(null);
            setShowItemForm(true);
          }}
        >
          + Add item
        </Button>
      </div>
      <Card className="overflow-hidden p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Category</TableHead>
              <TableHead>Item</TableHead>
              <TableHead className="text-right">Monthly</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {plan.data?.items.map((i) => (
              <TableRow key={i.id}>
                <TableCell>{i.category}</TableCell>
                <TableCell className="font-medium">{i.label}</TableCell>
                <TableCell className="text-right">
                  <Money amountMinor={i.monthly_amount_minor} native={i.currency} />
                </TableCell>
                <TableCell>
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setEditingItem(i);
                        setShowItemForm(true);
                      }}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (confirm("Delete?")) removeItem.mutate(i.id);
                      }}
                    >
                      Del
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {plan.data?.items.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} className="text-muted-foreground py-8 text-center">
                  No expense plan items.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      <Modal
        open={showTargetForm}
        onClose={() => {
          setShowTargetForm(false);
        }}
        title={editingTarget ? "Edit target" : "New target"}
      >
        <TargetForm
          initial={editingTarget}
          onDone={() => {
            setShowTargetForm(false);
            void qc.invalidateQueries({ queryKey: ["targets"] });
          }}
        />
      </Modal>

      <Modal
        open={showItemForm}
        onClose={() => {
          setShowItemForm(false);
        }}
        title={editingItem ? "Edit item" : "New expense plan item"}
      >
        <ExpenseItemForm
          initial={editingItem}
          onDone={() => {
            setShowItemForm(false);
            void qc.invalidateQueries({ queryKey: ["plan"] });
          }}
        />
      </Modal>
    </>
  );
}

function TargetForm({
  initial,
  onDone,
}: {
  initial: RetirementTarget | null;
  onDone: () => void;
}): JSX.Element {
  const [label, setLabel] = useState(initial?.label ?? "");
  const [amountMajor, setAmountMajor] = useState(
    initial ? minorToMajorInput(initial.fire_amount_minor) : "",
  );
  const [currency, setCurrency] = useState(initial?.currency ?? "USD");
  const [swr, setSwr] = useState(initial?.safe_withdrawal_rate ?? "0.0400");
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => {
      const body = {
        label,
        currency: currency.toUpperCase(),
        fire_amount_minor: majorToMinor(amountMajor),
        safe_withdrawal_rate: swr,
      };
      return initial
        ? api.update(`/retirement/targets/${initial.id}`, body)
        : api.create("/retirement/targets", body);
    },
    onSuccess: onDone,
    onError: (e: Error) => {
      setError(e.message);
    },
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <Field label="Label *">
        <TextInput
          required
          value={label}
          onChange={(e) => {
            setLabel(e.target.value);
          }}
          placeholder="Comfort"
        />
      </Field>
      <Field label="Amount *">
        <TextInput
          required
          type="number"
          step="0.01"
          value={amountMajor}
          onChange={(e) => {
            setAmountMajor(e.target.value);
          }}
        />
      </Field>
      <Field label="Currency *">
        <TextInput
          required
          maxLength={3}
          value={currency}
          onChange={(e) => {
            setCurrency(e.target.value.toUpperCase());
          }}
        />
      </Field>
      <Field label="Safe withdrawal rate (decimal)">
        <TextInput
          type="number"
          step="0.0001"
          value={swr}
          onChange={(e) => {
            setSwr(e.target.value);
          }}
        />
      </Field>
      {error && <p className="text-bad mb-3 text-sm">{error}</p>}
      <Button type="submit" disabled={save.isPending}>
        {save.isPending ? "Saving..." : "Save"}
      </Button>
    </form>
  );
}

function ExpenseItemForm({
  initial,
  onDone,
}: {
  initial: ExpensePlanItem | null;
  onDone: () => void;
}): JSX.Element {
  const [category, setCategory] = useState(initial?.category ?? "Housing");
  const [label, setLabel] = useState(initial?.label ?? "");
  const [amountMajor, setAmountMajor] = useState(
    initial ? minorToMajorInput(initial.monthly_amount_minor) : "",
  );
  const [currency, setCurrency] = useState(initial?.currency ?? "EUR");
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => {
      const body = {
        category,
        label,
        currency: currency.toUpperCase(),
        monthly_amount_minor: majorToMinor(amountMajor),
      };
      return initial
        ? api.update(`/retirement/expense-plan/${initial.id}`, body)
        : api.create("/retirement/expense-plan", body);
    },
    onSuccess: onDone,
    onError: (e: Error) => {
      setError(e.message);
    },
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <Field label="Category *">
        <TextInput
          required
          value={category}
          onChange={(e) => {
            setCategory(e.target.value);
          }}
          placeholder="Housing"
        />
      </Field>
      <Field label="Label *">
        <TextInput
          required
          value={label}
          onChange={(e) => {
            setLabel(e.target.value);
          }}
          placeholder="Rent"
        />
      </Field>
      <Field label="Monthly amount *">
        <TextInput
          required
          type="number"
          step="0.01"
          value={amountMajor}
          onChange={(e) => {
            setAmountMajor(e.target.value);
          }}
        />
      </Field>
      <Field label="Currency *">
        <TextInput
          required
          maxLength={3}
          value={currency}
          onChange={(e) => {
            setCurrency(e.target.value.toUpperCase());
          }}
        />
      </Field>
      {error && <p className="text-bad mb-3 text-sm">{error}</p>}
      <Button type="submit" disabled={save.isPending}>
        {save.isPending ? "Saving..." : "Save"}
      </Button>
    </form>
  );
}
